import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore, useTestStore } from '../store';
import { localApi } from '../utils/local-api';
import { TestSession } from '../types';
import { ChevronLeft, ChevronRight, PenTool, AlertCircle, Sparkles } from 'lucide-react';
import PartCountConfig from '../components/PartCountConfig';
import { getParts, getDefaults, parseLevel, snapToGroup } from '../config/hskTestParts';
import { api, API_BASE_URL, WritingGrading } from '../utils/api';
import {
  getHistoryScoreCardClass,
  getHistoryScorePanelClass,
  getHistoryScoreTextClass,
} from '../utils/historyScoreColors';

const WritingTest: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { level: levelParam } = useParams<{ level: string }>();
  const HSK_LEVEL = parseLevel(levelParam);
  const TEST_PART = 'writing';
  const parts = getParts(HSK_LEVEL, 'writing');

  const { token } = useAuthStore();
  const { currentTest: storeCurrentTest, startTest, nextQuestion, previousQuestion, completeTest, addTestToHistory, clearCurrentTest } = useTestStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedAnswerReady, setSelectedAnswerReady] = useState(false);
  const [initializingApi, setInitializingApi] = useState(true);
  const [isCloudflare, setIsCloudflare] = useState(false);
  const [cloudSessionId, setCloudSessionId] = useState<string | null>(null);
  const [completedSession, setCompletedSession] = useState<TestSession | null>(null);
  const [simulationCombinedSession, setSimulationCombinedSession] = useState<TestSession | null>(null);
  const [writingAnswers, setWritingAnswers] = useState<Record<number, { sequence?: string[]; text?: string }>>({});
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [isCheckpointSaving, setIsCheckpointSaving] = useState(false);
  const [checkpointError, setCheckpointError] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [writingConfig, setWritingConfig] = useState<Record<string, number>>(() => getDefaults(HSK_LEVEL, 'writing'));
  // AI grading state for HSK 4 W2 (keyed by question index)
  const [gradingResults, setGradingResults] = useState<Record<number, WritingGrading>>({});
  const [gradingLoading, setGradingLoading] = useState<Record<number, boolean>>({});
  const [gradingError, setGradingError] = useState<Record<number, string>>({});
  const [submittedSummary, setSubmittedSummary] = useState<{ rawScore: number; weightedScore: number; maxScore: number; total: number } | null>(null);
  const [submittedQuestionMeta, setSubmittedQuestionMeta] = useState<Record<number, { awardedScore?: number | null; aiScore?: number | null; maxScore?: number | null; grading?: WritingGrading | null; isCorrect?: boolean }>>({});
  const hasVerifiedWithServer = useRef(false);

  const handleGradeWriting = async (idx: number, prompt: string, answer: string) => {
    if (!token || gradingLoading[idx]) return;
    setGradingError(prev => ({ ...prev, [idx]: '' }));
    setGradingLoading(prev => ({ ...prev, [idx]: true }));
    try {
      const res = await api.gradeWriting({ prompt, answer }, token);
      setGradingResults(prev => ({ ...prev, [idx]: res.grading }));
    } catch (err: any) {
      setGradingError(prev => ({ ...prev, [idx]: err?.message || 'AI 批改失败，请稍后重试。' }));
    } finally {
      setGradingLoading(prev => ({ ...prev, [idx]: false }));
    }
  };

  const isSimulationMode = new URLSearchParams(location.search).get('sim') === '1';
  const isReviewMode = new URLSearchParams(location.search).get('review') === '1';
  const examScope = `hsk:2.0:simulation:${HSK_LEVEL}:${TEST_PART}:`;
  const currentTest = storeCurrentTest && (
    (isReviewMode && storeCurrentTest.testId?.startsWith('practice-')) || storeCurrentTest.examScope === examScope
  ) ? storeCurrentTest : null;

  // Whether a question is a sentence-ordering task (W1 in all levels).
  const isOrderQuestion = (q: any) => (q?.part === 'W1' || (q?.question && q.question.includes(';')));
  // HSK 4 W2 is AI-scored on submission, exactly like W2 in Full Simulation.
  const isLongForm = (q: any) =>
    Number(q?.level ?? HSK_LEVEL) === 4 && String(q?.part || '').toUpperCase() === 'W2';
  const getStoredQuestionMeta = (detail: any) => {
    const next: Record<number, { awardedScore?: number | null; aiScore?: number | null; maxScore?: number | null; grading?: WritingGrading | null; isCorrect?: boolean }> = {};
    (detail?.items || []).forEach((item: any, idx: number) => {
      const content = item?.answer_content;
      if (content && typeof content === 'object') {
        next[idx] = {
          awardedScore: typeof item?.awarded_score === 'number'
            ? item.awarded_score
            : (typeof content.awarded_score === 'number' ? content.awarded_score : null),
          aiScore: typeof content.ai_score === 'number' ? content.ai_score : null,
          maxScore: typeof content.max_score === 'number' ? content.max_score : null,
          grading: (content.ai_grading || null) as WritingGrading | null,
          isCorrect: !!item?.correct,
        };
      }
    });
    return next;
  };

  const resolveMediaUrl = (media?: string | null) => {
    if (!media) return '';
    if (/^https?:\/\//i.test(media)) return media;
    return `${API_BASE_URL}/media/${String(media).replace(/^\/+/, '')}`;
  };

  useEffect(() => {
    setWritingConfig(getDefaults(HSK_LEVEL, 'writing'));
  }, [HSK_LEVEL]);

  const initializeAndLoadQuestions = async (cfg: Record<string, number>) => {
    if (!token) return;

    const partCounts = parts
      .map(p => ({ part: p.key, count: snapToGroup(cfg[p.key] ?? 0, p.groupSize) }))
      .filter(item => item.count > 0);

    const totalQuestions = partCounts.reduce((sum, item) => sum + item.count, 0);
    if (totalQuestions <= 0) {
      setError('请至少设置 1 道写作题。');
      return;
    }

    try {
      setError('');
      setShowConfig(false);
      setInitializingApi(true);
      setLoading(true);

      const host = typeof window !== 'undefined' ? window.location.host : '';
      const cf = /workers\.dev|pages\.dev/i.test(host);
      setIsCloudflare(cf);
      const selectedSubparts = partCounts.map(item => item.part);

      if (cf) {
        // @ts-ignore
        const res = await import('../utils/api').then(m => m.api.startTest(HSK_LEVEL, TEST_PART, totalQuestions, token, partCounts));
        setCloudSessionId(res.test_session_id);
        const questions = res.questions.map((q: any) => ({
          id: q.question_id,
          level: q.level ?? HSK_LEVEL,
          part: q.part || TEST_PART,
          question: q.question_text,
          options: q.options,
          correctAnswer: -1,
          media: q.media,
          order: q.order
        }));

        let initialIndex = 0;
        const waInit: Record<number, { sequence?: string[]; text?: string }> = {};

        if (res.resumed && res.progress) {
          console.log("Resuming Cloudflare test session:", res.test_session_id);
          initialIndex = res.progress.index || 0;
          const pa = res.progress.answers || {};
          Object.entries(pa).forEach(([k, v]) => {
            const idx = Number(k);
            if (Array.isArray(v)) waInit[idx] = { sequence: v as string[] };
            else if (typeof v === 'string') waInit[idx] = { text: String(v) };
          });
        }

        setWritingAnswers(waInit);
        startTest(HSK_LEVEL, TEST_PART, questions, res.test_session_id, initialIndex, undefined, examScope);
        return;
      }

      const response = await localApi.startTest(HSK_LEVEL, TEST_PART, totalQuestions, token, selectedSubparts, partCounts);

      if (!response.success) {
        throw new Error('Failed to start test');
      }

      const questions = response.questions.map(q => ({
        id: q.question_id,
        level: (q as any).level ?? HSK_LEVEL,
        part: q.part || TEST_PART,
        question: q.question_text,
        options: q.options,
        correctAnswer: q.correct_index,
        media: q.media,
        order: q.order
      }));

      let initialIndex = 0;
      const waInit: Record<number, { sequence?: string[]; text?: string }> = {};
      if (response.resumed && response.progress) {
        initialIndex = response.progress.index || 0;
        const pa = response.progress.answers || {};
        Object.entries(pa).forEach(([k, v]) => {
          const idx = Number(k);
          if (Array.isArray(v)) waInit[idx] = { sequence: v as string[] };
          else if (typeof v === 'string') waInit[idx] = { text: String(v) };
        });
        setWritingAnswers(waInit);
      }
      startTest(HSK_LEVEL, TEST_PART, questions, response.test_id, initialIndex, undefined, examScope);
    } catch (err: any) {
      console.error('Failed to load Writing questions:', err);
      setError(err.message || 'Failed to load Writing questions');
    } finally {
      setLoading(false);
      setInitializingApi(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    // Review mode = "Review Wrong" navigated here with ?review=1, using the
    // in-memory practice session set by TestHistory. It must never start/resume
    // a server test and must never persist progress.
    const isReviewMode = new URLSearchParams(window.location.search).get('review') === '1';
    const currentIsPracticeSession = currentTest?.testId?.startsWith('practice-');

    if (isReviewMode && currentIsPracticeSession) {
      // Trust the practice session put in the store by startWrongPractice.
      setLoading(false);
      setInitializingApi(false);
      return;
    }

    // Coming back to a normal Writing Test while a stale practice session is
    // still in the store — clear it so we don't replay Review Wrong.
    if (!isReviewMode && currentIsPracticeSession) {
      clearCurrentTest();
      return;
    }

    const isMatchingTest = !currentIsPracticeSession &&
                           currentTest &&
                           currentTest.questions &&
                           currentTest.questions.length > 0 &&
                           currentTest.part === TEST_PART &&
                           currentTest.level === HSK_LEVEL;

    // Clear stale currentTest from a different test type (prevents wrong question count)
    if (!isMatchingTest && currentTest && !currentIsPracticeSession) {
      clearCurrentTest();
      return;
    }

    if (isMatchingTest) {
      if (!hasVerifiedWithServer.current) {
        // First mount: verify with server to detect cross-device completion
        hasVerifiedWithServer.current = true;
        (async () => {
          try {
            const activeRes = await localApi.getActiveTests(token);
            const isStillActive = (activeRes.active || []).some((t: any) => t.id === currentTest.testId);
            if (!isStillActive) {
              // Test completed on another device — reset so user can start fresh
              clearCurrentTest();
              return;
            }
          } catch {
            // Server check failed, trust localStorage as fallback
          }
          setLoading(false);
          setInitializingApi(false);
        })();
      } else {
        setLoading(false);
        setInitializingApi(false);
      }
      return;
    }

    if (isSimulationMode) {
      initializeAndLoadQuestions(getDefaults(HSK_LEVEL, 'writing'));
      return;
    }

    // Standalone practice: let the student choose how many questions per part.
    setShowConfig(true);
    setLoading(false);
    setInitializingApi(false);
  }, [token, currentTest, navigate, isSimulationMode, HSK_LEVEL]);

  useEffect(() => {
    if (currentTest && currentTest.testId && token && !currentTest.testId.startsWith('practice-')) {
      const idx = currentTest.currentQuestionIndex;
      const answersObj: Record<string, any> = {};
      Object.entries(writingAnswers).forEach(([k, v]) => {
        if ((v.sequence && v.sequence.length) || (v.text && v.text.trim().length)) {
          answersObj[k] = v.sequence ? v.sequence : v.text;
        }
      });
      localApi.saveProgress(currentTest.testId, idx, answersObj, token).catch(e => console.warn("Failed to auto-save", e));
    }
  }, [writingAnswers, currentTest?.currentQuestionIndex, currentTest?.testId, token]);

  const getTokens = (q: string) => q.split(';').map(s => s.trim()).filter(s => s.length > 0);

  const currentQObj = currentTest ? currentTest.questions[currentTest.currentQuestionIndex] : null;
  const currentTokens = currentQObj && isOrderQuestion(currentQObj) ? getTokens(currentQObj.question) : [];
  const usedTokens = writingAnswers[currentTest?.currentQuestionIndex || 0]?.sequence || [];

  const getTokenCounts = (tokens: string[]) => {
    const counts: Record<string, number> = {};
    tokens.forEach(t => counts[t] = (counts[t] || 0) + 1);
    return counts;
  };

  const totalCounts = getTokenCounts(currentTokens);
  const usedCounts = getTokenCounts(usedTokens);

  const remainingTokens: string[] = [];
  Object.keys(totalCounts).forEach(t => {
    const total = totalCounts[t];
    const used = usedCounts[t] || 0;
    const remaining = total - used;
    for(let i=0; i<remaining; i++) remainingTokens.push(t);
  });

  const selectToken = (t: string) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswers[idx] || {};
    const seq = [...(cur.sequence || []), t];
    const next = { ...writingAnswers, [idx]: { sequence: seq } };
    setWritingAnswers(next);
  };

  const removeToken = (indexToRemove: number) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswers[idx]?.sequence || [];
    const newSeq = cur.filter((_, i) => i !== indexToRemove);
    const next = { ...writingAnswers, [idx]: { sequence: newSeq } };
    setWritingAnswers(next);
  };

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, i: number) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>, overIndex: number) => {
    e.preventDefault();
    if (dragIndex === null) return;

    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswers[idx]?.sequence || [];

    if (dragIndex === overIndex) return;

    const newSeq = [...cur];
    const [moved] = newSeq.splice(dragIndex, 1);
    newSeq.splice(overIndex, 0, moved);

    const next = { ...writingAnswers, [idx]: { sequence: newSeq } };
    setWritingAnswers(next);
    setDragIndex(overIndex);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const val = e.target.value;
    const next = { ...writingAnswers, [idx]: { text: val } };
    setWritingAnswers(next);
  };

  const handleNext = async () => {
    if (isCheckpointSaving) return;

    if (currentTest && currentTest.currentQuestionIndex < currentTest.questions.length - 1) {
      const isPracticeSession = currentTest.testId?.startsWith('practice-');
      if ((currentTest.currentQuestionIndex + 1) % 10 === 0 && currentTest.testId && token && !isPracticeSession) {
        setIsCheckpointSaving(true);
        setCheckpointError('');

        try {
          const idx = currentTest.currentQuestionIndex;
          const answersObj: Record<string, any> = {};

          currentTest.answers.forEach((ans, i) => {
              if (ans !== null) answersObj[i] = ans;
          });

          Object.entries(writingAnswers).forEach(([k, v]) => {
              if ((v.sequence && v.sequence.length) || (v.text && v.text.trim().length)) {
                  answersObj[k] = v.sequence ? v.sequence : v.text;
              }
          });

          await localApi.saveProgress(currentTest.testId, idx, answersObj, token);
        } catch (err) {
          console.error("Checkpoint save failed:", err);
          setCheckpointError('网络连接异常，进度保存失败，请检查网络后重试。');
        } finally {
          setIsCheckpointSaving(false);
          nextQuestion();
        }
      } else {
        nextQuestion();
      }
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    previousQuestion();
  };

  const handleComplete = async () => {
    if (!currentTest) return;

    const isPracticeSession = currentTest.testId?.startsWith('practice-');
    const session = completeTest();
    if (session) {
      const sessionWithWritingAnswers: TestSession = {
        ...session,
        answers: session.questions.map((q, index) => {
          const wa = writingAnswers[index] || {};
          if (isOrderQuestion(q)) {
            return { sequence: wa.sequence || [] };
          }
          return { text: (wa.text || '').trim() };
        }),
      };

      // Optimistically update UI to show results immediately
      setCompletedSession(sessionWithWritingAnswers);
      setShowResults(true);
      setSubmittedSummary(null);
      setSubmittedQuestionMeta({});
      if (isPracticeSession) {
        addTestToHistory({
          ...sessionWithWritingAnswers,
          score: 0,
        });
        return;
      }

      const writingAnswersPayload = session.questions.map((q, index) => {
        const wa = writingAnswers[index] || {};
        const orderType = isOrderQuestion(q);
        if (orderType) {
          return {
            question_id: q.id,
            chosen_option_index: null,
            chose_buzhidao: false,
            time_spent_seconds: 60,
            chosen_sequence: (wa.sequence || []).length > 0 ? wa.sequence! : null
          };
        } else {
          return {
            question_id: q.id,
            chosen_option_index: null,
            chose_buzhidao: false,
            time_spent_seconds: 60,
            chosen_text_answer: (wa.text || '').trim().length > 0 ? (wa.text || '').trim() : null
          };
        }
      });

      if (token) {
        try {
          if (isCloudflare && cloudSessionId) {
             // @ts-ignore
             const response = await import('../utils/api').then(m => m.api.submitTest(cloudSessionId, writingAnswersPayload, token));
             setSubmittedSummary({
               rawScore: Number(response.score || 0),
               weightedScore: Number(response.weighted_score || 0),
               maxScore: Number(response.max_score || 0),
               total: Number(response.total || session.questions.length),
             });
             addTestToHistory(sessionWithWritingAnswers);
          } else {
             const response = await localApi.submitTest(session.id, writingAnswersPayload as any, token);
             if (response.success) {
               setSubmittedSummary({
                 rawScore: Number(response.score || 0),
                 weightedScore: Number(response.weighted_score || 0),
                 maxScore: Number(response.max_score || 0),
                 total: Number(response.total || session.questions.length),
               });
               try {
                 const detail = await api.getTestDetail(session.id, token);
                 const storedMeta = getStoredQuestionMeta(detail);
                 setSubmittedQuestionMeta(storedMeta);
                 const storedGradings: Record<number, WritingGrading> = {};
                 Object.entries(storedMeta).forEach(([key, value]) => {
                   if (value?.grading) storedGradings[Number(key)] = value.grading;
                 });
                 if (Object.keys(storedGradings).length > 0) {
                   setGradingResults(storedGradings);
                 }
               } catch (detailError) {
                 console.warn('Failed to hydrate stored writing grading:', detailError);
               }
               addTestToHistory(sessionWithWritingAnswers);
             }
          }
        } catch (err: any) {
          console.error('Failed to submit Writing test:', err);
          // Don't block UI on error, results are already shown
        } finally {
          if (isCloudflare) {
             navigate('/history');
          } else {
             const params = new URLSearchParams(window.location.search);
             if (params.get('sim') === '1') {
               // ... simulation submission logic ...
               try {
                 const history = useTestStore.getState().testHistory || [];
                 const latestListening = [...history].reverse().find(h => h.part === 'listening');
                 const latestReading = [...history].reverse().find(h => h.part === 'reading');
                 const latestWriting = sessionWithWritingAnswers;
                 const sessions = [latestListening, latestReading, latestWriting].filter(Boolean) as any[];
                 const combinedAnswers = sessions.flatMap(s => {
                   if (s.id === sessionWithWritingAnswers.id) {
                     return writingAnswersPayload;
                   }
                   return s.answers.map((ans: number|null, idx: number) => ({
                     question_id: s.questions[idx].id,
                     chosen_option_index: ans === 4 ? null : ans,
                     chose_buzhidao: ans === 4,
                     time_spent_seconds: 30
                   }));
                 });
                 const resp = await localApi.submitTest('hsk3_simulation_session', combinedAnswers, token);
                 if (resp.success) {
                   const combinedQuestions = sessions.flatMap(s => s.questions);
                   const combinedAns = sessions.flatMap(s => s.answers);
                   const simSession: TestSession = {
                     id: crypto.randomUUID(),
                     userId: '',
                     level: HSK_LEVEL,
                     part: 'simulation',
                     questions: combinedQuestions,
                     answers: combinedAns,
                     score: 0,
                     completedAt: new Date().toISOString(),
                     createdAt: new Date().toISOString()
                   } as any;
                   addTestToHistory(simSession);
                   setSimulationCombinedSession(simSession);
                 }
               } catch {}
             }
          }
        }
      }
    } else {
      setShowResults(true);
    }
  };

  useEffect(() => {
    if (!currentTest) return;
    const idx = currentTest.currentQuestionIndex;
    const q = currentTest.questions[idx];
    const wa = writingAnswers[idx];
    if (isOrderQuestion(q)) {
      // Ready if sequence length matches tokens length
      const ready = (wa?.sequence || []).length === getTokens(q.question).length;
      setSelectedAnswerReady(ready);
    } else {
      // Ready if text is not empty
      const ready = (wa?.text || '').trim().length > 0;
      setSelectedAnswerReady(ready);
    }
  }, [currentTest?.currentQuestionIndex, writingAnswers, currentTest?.questions]);

  if (loading || initializingApi) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {initializingApi ? 'Initializing...' : 'Loading Writing Test...'}
          </p>
        </div>
      </div>
    );
  }

  if (error && !showConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 text-red-500 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Error</h2>
          </div>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResults && simulationCombinedSession) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
               <h2 className="text-2xl font-bold text-gray-800">HSK {HSK_LEVEL} Simulation Results</h2>
               <p>Simulation complete. Please check your profile for detailed history.</p>
               <button onClick={() => navigate('/dashboard')} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded">Back to Dashboard</button>
            </div>
          </div>
        </div>
    );
  }

  if (showResults && completedSession) {
    const qs = completedSession.questions;
    const total = qs.length;
    const rawScore = submittedSummary?.rawScore ?? qs.reduce((acc, q, idx) => {
      const wa = writingAnswers[idx] || {};
      if (isOrderQuestion(q)) {
        let correctSeq: string[] = [];
        const opts = q.options;
        const order = (q as any).order;

        if (order && Array.isArray(order) && order.length === opts.length) {
            correctSeq = new Array(opts.length);
            for (let k = 0; k < opts.length; k++) {
                correctSeq[order[k]] = String(opts[k]).trim();
            }
        } else {
            correctSeq = q.options.map(s => String(s).trim());
        }

        correctSeq = correctSeq.filter(s => s.length > 0);

        const userSeq = (wa.sequence || []).map(s => String(s).trim());
        const ok = correctSeq.length === userSeq.length && correctSeq.every((t, i) => t === userSeq[i]);
        return acc + (ok ? 1 : 0);
      } else if (isLongForm(q)) {
        const meta = submittedQuestionMeta[idx];
        return acc + (meta?.isCorrect ? 1 : 0);
      } else {
        const correct = (q.options[0] || '').trim();
        const user = (wa.text || '').trim();
        return acc + (user.length > 0 && user === correct ? 1 : 0);
      }
    }, 0);
    const weightedScore = submittedSummary?.weightedScore ?? rawScore;
    const maxScore = submittedSummary?.maxScore ?? total;
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">HSK {HSK_LEVEL}级 书写测试 回顾</h2>
              <div className="text-right">
                <div className="text-xl font-bold text-blue-600">{weightedScore}/{maxScore}</div>
                <div className="text-sm text-gray-600">{Math.round(((weightedScore || 0) / Math.max(maxScore || 1, 1)) * 100)}%</div>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            {completedSession.questions.map((qItem, idx) => {
              const wa = writingAnswers[idx] || {};
              const storedMeta = submittedQuestionMeta[idx] || {};
              const orderType = isOrderQuestion(qItem);
              const longForm = isLongForm(qItem);
              const awardedScore = typeof storedMeta.awardedScore === 'number' ? Number(storedMeta.awardedScore || 0) : null;
              const aiScore = typeof storedMeta.aiScore === 'number' ? Number(storedMeta.aiScore || 0) : null;
              const maxQuestionScore = typeof storedMeta.maxScore === 'number' ? Number(storedMeta.maxScore || 0) : 8;
              const scorePercentage = aiScore != null
                ? aiScore
                : (awardedScore != null && maxQuestionScore > 0 ? (awardedScore / maxQuestionScore) * 100 : 0);
              let cardCorrect = false;
              let cardUnknown = false;
              if (orderType) {
                let correctSeq: string[] = [];
                const opts = qItem.options;
                const order = (qItem as any).order;
                if (order && Array.isArray(order) && order.length === opts.length) {
                    correctSeq = new Array(opts.length);
                    for (let k = 0; k < opts.length; k++) {
                        correctSeq[order[k]] = String(opts[k]).trim();
                    }
                } else {
                    correctSeq = opts.map(s => String(s).trim());
                }

                correctSeq = correctSeq.filter(s => s.length > 0);

                const userSeq = (wa.sequence || []).map(s => String(s).trim());
                cardCorrect = correctSeq.length === userSeq.length && correctSeq.every((t, i) => t === userSeq[i]);
                cardUnknown = userSeq.length === 0;
              } else if (longForm) {
                const user = (wa.text || '').trim();
                cardUnknown = user.length === 0;
                cardCorrect = typeof storedMeta.isCorrect === 'boolean' ? !!storedMeta.isCorrect : !cardUnknown;
              } else {
                const correct = (qItem.options[0] || '').trim();
                const user = (wa.text || '').trim();
                cardCorrect = user.length > 0 && user === correct;
                cardUnknown = user.length === 0;
              }
              const cardClass = longForm
                ? (cardUnknown ? 'border-orange-300 ring-2 ring-orange-200' : getHistoryScoreCardClass(scorePercentage))
                : cardCorrect ? 'border-green-500 ring-2 ring-green-400' : 'border-red-500 ring-2 ring-red-400';
              return (
                <div key={idx} className={`p-6 border rounded-lg ${cardClass}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-gray-600">第 {idx + 1} 题</div>
                    <div className="text-sm">
                      {longForm ? (
                        cardUnknown ? <span className="text-orange-600 font-semibold">未作答</span> : <span className={`${getHistoryScoreTextClass(scorePercentage)} font-semibold`}>AI 已评分{awardedScore != null ? ` ${awardedScore}/${maxQuestionScore}` : ''}</span>
                      ) : cardCorrect ? (
                        <span className="text-green-600 font-semibold">回答正确</span>
                      ) : cardUnknown ? (
                        <span className="text-red-600 font-semibold">不知道</span>
                      ) : (
                        <span className="text-red-600 font-semibold">回答错误</span>
                      )}
                    </div>
                  </div>
                  <div className="mb-4 text-lg font-semibold text-gray-800 whitespace-pre-line">{qItem.question}</div>
                  {longForm && qItem.media && (
                    <div className="mb-4">
                      <img
                        src={resolveMediaUrl(qItem.media)}
                        alt={`HSK ${HSK_LEVEL} Writing prompt ${idx + 1}`}
                        className="max-h-80 w-auto rounded-lg border border-gray-200 bg-gray-50 object-contain"
                      />
                    </div>
                  )}
                  {orderType ? (
                    <div className="space-y-3">
                      <div className="p-4 border rounded">
                        <div className="text-sm text-gray-600 mb-2">你的答案</div>
                        <div className="flex flex-wrap gap-2">
                          {(wa.sequence || []).map((t, i) => (
                            <span key={t + i} className="px-2 py-1 border rounded bg-gray-50">{t}</span>
                          ))}
                        </div>
                      </div>
                      <div className="p-4 border rounded">
                        <div className="text-sm text-gray-600 mb-2">正确答案</div>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                             let correctSeq: string[] = [];
                             const opts = qItem.options;
                             const order = (qItem as any).order;
                             if (order && Array.isArray(order) && order.length === opts.length) {
                                 correctSeq = new Array(opts.length);
                                 for (let k = 0; k < opts.length; k++) {
                                     correctSeq[order[k]] = String(opts[k]).trim();
                                 }
                             } else {
                                 correctSeq = opts.map(s => String(s).trim());
                             }
                             correctSeq = correctSeq.filter(s => s.length > 0);
                             return correctSeq.map((t, i) => (
                               <span key={String(t) + i} className="px-2 py-1 border rounded bg-green-50">{t}</span>
                             ));
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-4 border rounded">
                        <div className="text-sm text-gray-600 mb-2">你的答案</div>
                        <div className="text-gray-800 whitespace-pre-line">{(wa.text || '').trim() || '未作答'}</div>
                      </div>
                      {!longForm && (
                        <div className="p-4 border rounded">
                          <div className="text-sm text-gray-600 mb-2">正确答案</div>
                          <div className="text-green-700">{(qItem.options[0] || '').trim()}</div>
                        </div>
                      )}
                      {longForm && (wa.text || '').trim().length > 0 && (
                        <div className={`p-4 border rounded ${getHistoryScorePanelClass(scorePercentage)}`}>
                          {awardedScore != null && (
                            <div className="mb-3 flex items-center justify-between rounded-xl bg-white px-4 py-3 border border-indigo-100">
                              <div>
                                <div className={`text-sm font-bold ${getHistoryScoreTextClass(scorePercentage)}`}>AI 计分</div>
                                <div className="text-xs text-gray-500">本题最终得分已按 AI 百分比分数折算</div>
                              </div>
                              <div className="text-right">
                                <div className={`text-2xl font-bold ${getHistoryScoreTextClass(scorePercentage)}`}>{awardedScore}<span className="text-sm text-gray-400">/{maxQuestionScore}</span></div>
                                {aiScore != null && <div className={`text-xs ${getHistoryScoreTextClass(scorePercentage)}`}>AI: {aiScore}/100</div>}
                              </div>
                            </div>
                          )}
                          {!gradingResults[idx] ? (
                            <div>
                              <button
                                onClick={() => handleGradeWriting(idx, qItem.question, (wa.text || '').trim())}
                                disabled={gradingLoading[idx]}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#4F46E5] text-white font-bold text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Sparkles className="w-4 h-4" />
                                {gradingLoading[idx] ? 'AI 批改中…' : '重新获取 AI 分析'}
                              </button>
                              {gradingError[idx] && (
                                <div className="mt-2 text-sm text-red-600">{gradingError[idx]}</div>
                              )}
                            </div>
                          ) : (() => {
                            const g = gradingResults[idx];
                            return (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <div className={`flex items-center gap-2 text-sm font-bold ${getHistoryScoreTextClass(Number(g.score ?? scorePercentage))}`}><Sparkles className="w-4 h-4" /> AI 批改结果</div>
                                  {typeof g.score === 'number' && (
                                    <div className={`text-2xl font-bold ${getHistoryScoreTextClass(g.score)}`}>{g.score}<span className="text-sm text-gray-400">/100</span></div>
                                  )}
                                </div>
                                {g.summary && <div className="text-gray-700 text-sm">{g.summary}</div>}
                                {Array.isArray(g.dimensions) && g.dimensions.length > 0 && (
                                  <div className="grid grid-cols-2 gap-2">
                                    {g.dimensions.map((d, i) => (
                                      <div key={i} className="bg-white rounded p-2 text-sm border border-indigo-50">
                                        <div className="flex justify-between">
                                          <span className="font-semibold text-gray-700">{d.name}</span>
                                          {typeof d.score === 'number' && <span className={`${getHistoryScoreTextClass(d.score)} font-bold`}>{d.score}</span>}
                                        </div>
                                        {d.comment && <div className="text-gray-500 text-xs mt-1">{d.comment}</div>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {Array.isArray(g.corrections) && g.corrections.length > 0 && (
                                  <div className="space-y-2">
                                    <div className="text-sm font-semibold text-gray-700">修改建议</div>
                                    {g.corrections.map((c, i) => (
                                      <div key={i} className="text-sm border-l-2 border-amber-300 pl-2">
                                        <span className="text-red-500 line-through">{c.original}</span> → <span className="text-green-700">{c.suggestion}</span>
                                        {c.reason && <div className="text-gray-500 text-xs">{c.reason}</div>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {g.sample && (
                                  <div className="bg-green-50 rounded p-3">
                                    <div className="text-sm font-semibold text-green-800 mb-1">参考范文</div>
                                    <div className="text-gray-700 text-sm whitespace-pre-line">{g.sample}</div>
                                  </div>
                                )}
                                <button
                                  onClick={() => handleGradeWriting(idx, qItem.question, (wa.text || '').trim())}
                                  disabled={gradingLoading[idx]}
                                  className="text-xs text-gray-400 hover:text-gray-600"
                                >
                                  {gradingLoading[idx] ? '重新批改中…' : '重新批改'}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button onClick={() => navigate('/history')} className="mt-6 w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold transition-colors">
            Back to History
          </button>
        </div>
      </div>
    );
  }

  if (showConfig && !currentTest) {
    return (
      <PartCountConfig
        title="自定义书写题量"
        subtitle="设置每个部分要练习的题目数量，然后开始写作测试。"
        Icon={PenTool}
        parts={parts}
        config={writingConfig}
        onChange={setWritingConfig}
        onStart={() => initializeAndLoadQuestions(writingConfig)}
        startLabel="开始写作测试"
        error={error}
      />
    );
  }

  if (!currentTest) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = currentTest.questions[currentTest.currentQuestionIndex];
  const progress = ((currentTest.currentQuestionIndex + 1) / currentTest.questions.length) * 100;
  const longFormCurrent = isLongForm(currentQuestion);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <PenTool className="h-6 w-6 text-blue-600 mr-2" />
              <h1 className="text-2xl font-bold text-gray-800">HSK {HSK_LEVEL} Writing Test</h1>
            </div>
            <div className="text-sm text-gray-600">
              Question {currentTest.currentQuestionIndex + 1} of {currentTest.questions.length}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          {!isOrderQuestion(currentQuestion) && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 whitespace-pre-line">
                {currentQuestion.question}
              </h2>
              {longFormCurrent && currentQuestion.media && (
                <img
                  src={resolveMediaUrl(currentQuestion.media)}
                  alt={`HSK ${HSK_LEVEL} Writing prompt ${currentTest.currentQuestionIndex + 1}`}
                  className="max-h-96 w-auto rounded-lg border border-gray-200 bg-gray-50 object-contain"
                />
              )}
            </div>
          )}

          {isOrderQuestion(currentQuestion) ? (
            <div className="space-y-4">
              <div className="mb-2 text-sm text-gray-600">点击词语添加到答案区，拖动调整顺序</div>
              <div className="flex flex-wrap gap-2">
                {remainingTokens.map((t, i) => (
                  <button key={t + i} onClick={() => selectToken(t)} className="px-3 py-1 border rounded bg-gray-50 hover:bg-gray-100 transition-colors">
                    {t}
                  </button>
                ))}
              </div>
              <div className="mt-4 p-3 border rounded min-h-16 bg-gray-50">
                <div className="flex flex-wrap gap-2">
                  {(writingAnswers[currentTest.currentQuestionIndex]?.sequence || []).map((t, i) => (
                    <div
                      key={t + i}
                      draggable
                      onDragStart={(e) => onDragStart(e, i)}
                      onDragOver={(e) => onDragOver(e, i)}
                      className="px-3 py-1 border rounded bg-blue-100 cursor-move hover:bg-blue-200 transition-colors flex items-center"
                    >
                      {t}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeToken(i); }}
                        className="ml-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {(writingAnswers[currentTest.currentQuestionIndex]?.sequence || []).length === 0 && (
                    <span className="text-gray-400 italic">点击上方词语作答...</span>
                  )}
                </div>
              </div>
            </div>
          ) : longFormCurrent ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">请根据图片和提示词写一个有意义的完整句子（至少 10 个汉字，通常 10–12 个即可）：</label>
              <textarea
                rows={6}
                value={writingAnswers[currentTest.currentQuestionIndex]?.text || ''}
                onChange={onInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-y"
                placeholder="在此输入一个完整句子..."
              />
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">请输入答案：</label>
              <input
                type="text"
                value={writingAnswers[currentTest.currentQuestionIndex]?.text || ''}
                onChange={onInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="在此输入句子..."
              />
            </div>
          )}
        </div>

        {checkpointError && (
          <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
            {checkpointError}
          </div>
        )}

        {/* Navigation */}
        <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentTest.currentQuestionIndex === 0 || isCheckpointSaving}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                currentTest.currentQuestionIndex === 0 || isCheckpointSaving
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              }`}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>

            <button
              onClick={handleNext}
              disabled={!selectedAnswerReady || isCheckpointSaving}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                !selectedAnswerReady || isCheckpointSaving
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : currentTest.currentQuestionIndex === currentTest.questions.length - 1
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isCheckpointSaving ? 'Saving...' : currentTest.currentQuestionIndex === currentTest.questions.length - 1 ? 'Complete' : 'Next'}
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WritingTest;
